import React, { useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import './Roadmap.css';

const steps = [
  {
    title: 'The First Prototype',
    date: 'Fall 2024',
    text: "PointeSense began with a simple idea: to see inside a pointe shoe. During the Fall of 2024, Christine Fernandez and her team in the Principles of Design of BME Instrumentation course at Johns Hopkins University brought that idea to life by creating the first prototype, which combined two force sensors with an Arduino.",
    image: `${process.env.PUBLIC_URL}/images/prototype.jpg`,
  },
  {
    title: 'PointeSense V1',
    isWordmarkTitle: true,
    date: 'Spring 2025',
    text: 'With support from Ariel Slepyan and future co-founder Kaitlyn Kumar, we brought the first functional prototype of PointeSense to life. This initial version included a custom-built sensor that was ultra-thin and a haptic anklet that gave dancers real-time feedback as they moved.',
    image: `${process.env.PUBLIC_URL}/images/ASME_2.jpg`,
  },
  {
    title: 'PointeSense MVP',
    isWordmarkTitle: true,
    date: 'Summer 2025',
    text: 'After facing some challenges with fragile sensors during testing, we refined PointeSense into a smaller, wireless design that rests comfortably on the arch of the foot. This Minimum Viable Product (MVP) finally showcased the exciting potential of PointeSense for fitting pointe shoes and analyzing movement.',
    image: `${process.env.PUBLIC_URL}/images/DanceMagazine.png`,
  },
  {
    title: 'PAVA Center Fuel Accelerator',
    date: 'Fall 2025',
    text: "In the Fall of 2025, PointeSense became part of the PAVA Center's Fuel Accelerator program, marking a significant transition from concept to company. This experience allowed us to strengthen our vision and fine-tune our product further.",
    image: `${process.env.PUBLIC_URL}/images/PAVA.png`,
  },
  {
    title: 'Incorporation and BII',
    date: 'Jan 2026',
    text: 'In January 2026, PointeSense, LLC was officially formed. We are excited to join the Baltimore Innovation Initiative (BII) Pilot Program’s Technology Advancement Award, as we prepare for pilot testing within the dance community.',
    image: `${process.env.PUBLIC_URL}/PS_logodark.PNG`,
  },
  {
    title: '1st Place, HopStart',
    date: 'Spring 2026',
    text: 'In April 2026, PointeSense won 1st Place in the General Ventures II category at the 2026 Hopkins Student New Venture Challenge (HopStart).',
    image: `${process.env.PUBLIC_URL}/images/Hopstart.jpg`,
  },
  {
    title: 'Carey Student Venture Showcase',
    text: 'Also in April 2026, PointeSense pitched as one of six startups at the 2026 Carey Student Venture Showcase.',
    image: `${process.env.PUBLIC_URL}/images/MD_student.jpg`,
  },
  {
    title: 'Major Hardware Upgrades',
    date: 'Summer 2026',
    text: 'In Summer 2026, we made major technical changes to the sensor and Core: over a 50% reduction in Core size, a 50% increase in the number of sensors, and a new mobile app interface.',
    image: `${process.env.PUBLIC_URL}/images/PSV3_2feet.PNG`,
  },
  {
    title: 'Beginning Pilots',
    date: 'Fall 2026',
    text: 'Starting Fall 2026, we’re beginning pilots for pointe shoe fit beta testing and research collaborations.',
    image: `${process.env.PUBLIC_URL}/images/Logo_Dark_Circle.png`,
  },
];

// Builds a smoothed SVG path through a series of points. Segments that jump to a
// new row (a big vertical shift, from wrapping) get an exaggerated S-curve swoop
// instead of a straight diagonal; segments within the same row get gentle
// Catmull-Rom smoothing.
const buildSmoothPath = (points) => {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const isRowWrap = Math.abs(p2.y - p1.y) > 20;

    if (isRowWrap) {
      const dy = p2.y - p1.y;
      const c1x = p1.x;
      const c1y = p1.y + dy * 0.65;
      const c2x = p2.x;
      const c2y = p2.y - dy * 0.65;
      d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
    } else {
      const p0 = points[i === 0 ? i : i - 1];
      const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
    }
  }
  return d;
};

const Roadmap = () => {
  const containerRef = useRef(null);
  const nodeRefs = useRef([]);
  const [pathData, setPathData] = useState('');
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [activeIndex, setActiveIndex] = useState(null);

  const recomputePath = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const points = nodeRefs.current
      .filter(Boolean)
      .map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2 - containerRect.left,
          y: rect.top + rect.height / 2 - containerRect.top,
        };
      });
    setSvgSize({ width: containerRect.width, height: containerRect.height });
    setPathData(buildSmoothPath(points));
  }, []);

  useLayoutEffect(() => {
    recomputePath();
    window.addEventListener('resize', recomputePath);
    return () => window.removeEventListener('resize', recomputePath);
  }, [recomputePath]);

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveIndex(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeIndex]);

  const activeStep = activeIndex !== null ? steps[activeIndex] : null;

  return (
    <div className="roadmap-container">
      <div
        className="roadmap-hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/IMG_9401.jpg)` }}
      >
        <div className="roadmap-hero-overlay">
          <h1 className="roadmap-hero-title">Our Roadmap</h1>
        </div>
      </div>

      <section className="timeline-section">
        <div className="roadmap-content-wrapper">
          <div className="timeline-row" ref={containerRef}>
            <svg className="timeline-thread" width={svgSize.width} height={svgSize.height}>
              <path d={pathData} fill="none" stroke="var(--ps-darkblue)" strokeWidth="3" />
            </svg>

            {steps.map((step, index) => (
              <div className="timeline-node" key={step.title}>
                {step.date && <span className="timeline-date">{step.date}</span>}
                <button
                  type="button"
                  className="timeline-bubble"
                  ref={(el) => (nodeRefs.current[index] = el)}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`View: ${step.title}`}
                >
                  <img
                    src={step.image}
                    alt={step.title}
                    onLoad={recomputePath}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {activeStep && (
        <div className="roadmap-modal-overlay" onClick={() => setActiveIndex(null)}>
          <div className="roadmap-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="roadmap-modal-close"
              onClick={() => setActiveIndex(null)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="roadmap-modal-image-wrap">
              <img src={activeStep.image} alt={activeStep.title} className="roadmap-modal-image" />
            </div>
            <div className="roadmap-modal-body">
              {activeStep.date && <p className="roadmap-modal-date">{activeStep.date}</p>}
              <h3 className="roadmap-modal-title">
                {activeStep.isWordmarkTitle ? (
                  <>
                    <span className="wordmark-pointe">Pointe</span>
                    <span className="wordmark-sense-navy">Sense</span>
                    {activeStep.title.replace('PointeSense', '')}
                  </>
                ) : (
                  activeStep.title
                )}
              </h3>
              <p className="roadmap-modal-text">{activeStep.text}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Roadmap;
